import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import styled from "@emotion/styled";
import { useLocation, useNavigate } from "react-router-dom";
import PhotosCompanion from "@components/Home/PhotosCompanion/PhotosCompanion";
import BasicCompanion from "@components/Home/BasicCompanion/BasicCompanion";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@store/store";
import CompanionToolbar from "@components/CompanionToolbar";
import { useMediaQuery } from "react-responsive";
import {
  useCloseSessionMutation,
  useCreateSessionMutation,
  useStartSessionMutation,
} from "@store/sessionApi";
import { setMessage } from "@store/snackbarSlice";
import Companion from "@components/Companion/Companion";
import { Session } from "@models/session";
import { MutationActionCreatorResult } from "@reduxjs/toolkit/query";
import { ActivityType } from "@models/activity-type";
import { setMuted } from "@store/avatarSlice";
import { setOpen } from "@store/incomingCallSlice";
import { setOpen as setSessionTimedOutDialog } from "@store/sessionTimedOutDialogSlice";
import { SessionStartTime } from "@models/session-start-time";
import { useGetPlanSubscriptionDetailsQuery } from "@store/planApi";
import { isCallTimeLimitReached, isExpired } from "@utils/subscription.util";
import Loader from "@components/Loader/Loader";
import { useGetAppConfigQuery } from "@store/appConfigApi";
import { CompanionConversationProvider } from "@components/Companion/CompanionConversationContext";

const SUBSCRIPTION_RETRY_WINDOW_MS = 30_000;
const SUBSCRIPTION_RETRY_INTERVAL_MS = 2_000;

const Layout = styled(Stack)`
  background-color: #171a1fff;
  width: 100%;
  height: 100%;
`;

const Main = styled.main`
  display: flex;
  flex: 1;
  justify-content: center;
  align-items: center;
  overflow: hidden;
`;

const Talk: FC<{ isPatient: boolean }> = ({ isPatient }) => {
  const dispatch = useDispatch();
  const location = useLocation();

  const patientId = useSelector(
    (state: RootState) => state.home.selectedPatientId,
  ) || location.state?.patientId;
  const {
    data: subscription,
    isLoading: isSubscriptionLoading,
    isError: isSubscriptionError,
    isFetching: isSubscriptionFetching,
    refetch: refetchSubscription,
  } = useGetPlanSubscriptionDetailsQuery(
    { patientId: patientId! },
    { skip: !patientId },
  );

  const subscriptionPending =
    Boolean(patientId) &&
    !subscription &&
    (isSubscriptionLoading || isSubscriptionFetching);
  const ready = useSelector((state: RootState) => state.avatar.ready);
  const { data: appConfig } = useGetAppConfigQuery();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isLandscape = useMediaQuery({
    query: "screen and (orientation:landscape)",
  });

  const { albumId, reminderId, activityType } = location.state || {};

  const [createSessionApi] = useCreateSessionMutation();
  const [startSessionApi] = useStartSessionMutation();
  const [closeSessionApi, { isLoading: isStopping }] =
    useCloseSessionMutation();
  const sessionRef = useRef<Session | null>();
  const [sessionStartTime, setSessionStartTime] =
    useState<SessionStartTime | null>(null);
  const createSessionRequest = useRef<MutationActionCreatorResult<any> | null>(
    null,
  );
  const startSessionRequest = useRef<MutationActionCreatorResult<any> | null>(
    null,
  );
  const closingSessionId = useRef<string | null>(null);
  const retryFallbackNotified = useRef(false);
  const [subscriptionRetryStartedAt, setSubscriptionRetryStartedAt] = useState<
    number | null
  >(null);

  const closeSession = useCallback(
    async (sessionId: string): Promise<void> => {
      if (!sessionId || closingSessionId.current === sessionId) {
        return;
      }
      closingSessionId.current = sessionId;
      const { error } = await closeSessionApi(sessionId);
      if (!error) {
        sessionRef.current = null;
      }
      closingSessionId.current = null;
    },
    [closeSessionApi],
  );

  const createSession = useCallback(async (): Promise<void> => {
    if (!patientId || createSessionRequest.current) {
      return;
    }
    dispatch(setMuted(false));
    createSessionRequest.current = createSessionApi({
      patientId,
      isCaregiver: !isPatient,
      reminderId,
      activityType,
    });
    const { data, error } = await createSessionRequest.current;
    if (!data || error) {
      dispatch(setMessage({ content: t(`errors.unknown`), severity: "error" }));
    }
    sessionRef.current = data as Session;
    createSessionRequest.current = null;
  }, [
    t,
    dispatch,
    patientId,
    createSessionApi,
    reminderId,
    activityType,
    isPatient,
  ]);

  const stop = useCallback(async () => {
    if (!sessionRef.current?.id) {
      return;
    }
    closeSession(sessionRef.current.id);
    navigate(isPatient ? "/portal" : "/");
  }, [closeSession, navigate, isPatient]);

  const checkSessionAllowed = useCallback(() => {
    if (!subscription) {
      return false;
    }
    if (isExpired(subscription)) {
      dispatch(
        setMessage({
          content: t(`errors.subExpired`),
          severity: "error",
        }),
      );
      return false;
    }
    if (isCallTimeLimitReached(subscription)) {
      dispatch(
        setMessage({ content: t(`errors.maxMinutes`), severity: "error" }),
      );
      return false;
    }
    return true;
  }, [subscription, dispatch, t]);

  const hasRetryableSubscriptionFailure =
    Boolean(patientId) &&
    !subscriptionPending &&
    (isSubscriptionError || !subscription);
  const retryWindowOpen =
    subscriptionRetryStartedAt !== null &&
    Date.now() - subscriptionRetryStartedAt < SUBSCRIPTION_RETRY_WINDOW_MS;

  useEffect(() => {
    if (!hasRetryableSubscriptionFailure) {
      setSubscriptionRetryStartedAt(null);
      retryFallbackNotified.current = false;
      return;
    }
    if (subscriptionRetryStartedAt !== null) {
      return;
    }
    setSubscriptionRetryStartedAt(Date.now());
  }, [hasRetryableSubscriptionFailure, subscriptionRetryStartedAt]);

  useEffect(() => {
    if (!hasRetryableSubscriptionFailure || !retryWindowOpen) {
      return;
    }
    const timer = window.setTimeout(() => {
      refetchSubscription();
    }, SUBSCRIPTION_RETRY_INTERVAL_MS);
    return () => window.clearTimeout(timer);
  }, [hasRetryableSubscriptionFailure, retryWindowOpen, refetchSubscription]);

  useEffect(() => {
    if (!patientId) {
      navigate(isPatient ? "/portal" : "/");
      return;
    }

    if (subscriptionPending) {
      return;
    }

    if (hasRetryableSubscriptionFailure) {
      if (retryWindowOpen) {
        return;
      }
      if (retryFallbackNotified.current) {
        return;
      }
      retryFallbackNotified.current = true;
      dispatch(
        setMessage({ content: t(`errors.unknown`), severity: "error" }),
      );
      navigate(isPatient ? "/portal" : "/");
      return;
    }

    if (!checkSessionAllowed()) {
      navigate(isPatient ? "/portal" : "/");
      return;
    }

    createSession();

    return () => {
      if (sessionRef.current) {
        closeSession(sessionRef.current.id);
      }
    };
  }, [
    patientId,
    subscriptionPending,
    isSubscriptionError,
    subscription,
    createSession,
    closeSession,
    checkSessionAllowed,
    navigate,
    isPatient,
    dispatch,
    t,
    hasRetryableSubscriptionFailure,
    retryWindowOpen,
  ]);

  useEffect(() => {
    if (reminderId && ready) {
      dispatch(setOpen(false));
    }
  }, [ready, dispatch, reminderId]);

  const startSession = useCallback(async () => {
    if (startSessionRequest.current) {
      return;
    }
    if (!sessionRef.current?.id) {
      dispatch(setMessage({ content: t(`errors.unknown`), severity: "error" }));
      return;
    }
    startSessionRequest.current = startSessionApi(sessionRef.current.id);

    const { data, error } = await startSessionRequest.current;
    if (!data || error) {
      dispatch(setMessage({ content: t(`errors.unknown`), severity: "error" }));
    }
    setSessionStartTime(data as SessionStartTime);
    startSessionRequest.current = null;
  }, [startSessionApi, dispatch, t]);

  const onTimeOut = useCallback(async () => {
    await stop();
    dispatch(setSessionTimedOutDialog(true));
  }, [stop, dispatch]);

  useEffect(() => {
    if (!ready) {
      return;
    }
    startSession();
  }, [ready, startSession]);

  useEffect(() => {
    window.Argoli.Brevo.hideHelpButton();

    return () => {
      window.Argoli.Brevo.showHelpButton();
    };
  }, []);

  const startTime = sessionStartTime
    ? new Date(sessionStartTime.startTime)
    : null;

  if (!patientId || !appConfig || subscriptionPending || retryWindowOpen) {
    return (
      <Layout flexDirection={isLandscape ? "row" : "column"}>
        <Main>
          <Loader />
        </Main>
      </Layout>
    );
  }

  return (
    <CompanionConversationProvider appConfig={appConfig}>
      <Layout flexDirection={isLandscape ? "row" : "column"}>
        {sessionRef.current && (
          <CompanionToolbar
            vertical={isLandscape}
            showMenu={false}
            onStop={stop}
            stopping={isStopping}
            startTime={startTime}
          />
        )}

        <Main>
          {subscription && (
            <Companion
              showLoading={!reminderId}
              companion={sessionRef.current?.companion}
              startTime={startTime}
              remainingTalkSeconds={subscription.remainingLimits.talkSeconds}
              onClose={stop}
              onTimeout={onTimeOut}
            >
              {sessionRef.current ? (
                <>
                  {!!albumId || activityType === ActivityType.GallerySession ? (
                    <PhotosCompanion
                      albumId={albumId}
                      sessionId={sessionRef.current.id}
                      companionName={sessionRef.current.companion?.name || ""}
                    />
                  ) : (
                    <BasicCompanion
                      sessionId={sessionRef.current.id}
                      companionName={sessionRef.current.companion?.name || ""}
                    />
                  )}
                </>
              ) : null}
            </Companion>
          )}
        </Main>
      </Layout>
    </CompanionConversationProvider>
  );
};

export default Talk;
